import type { UseQueryOptions, UseQueryResult } from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";
import type { AxiosError } from "axios";

import { RefreshClient, useMergeQuery } from "@citrine/client";

import type { IProject } from "../project";

export interface DataServiceProjectCounts {
  terminal_materials_count: number;
  tables_count: number;
}

interface DataServiceMetricResponse {
  project_stats: Partial<Record<string, DataServiceProjectCounts>>;
}

interface DatasetSummary {
  project_id: string;
  team_id: string;
  name: string;
  id: string;
}
export interface DatasetRecentlyUpdatedResponse {
  dataset_summary: DatasetSummary;
  created_count: number;
  updated_count: number;
  most_recent_activity_time: number;
}

export interface DatasetMetric {
  most_recent_activity_time: number;
  dataset_summary: DatasetSummary;
  terminal_materials_count: number;
}
export interface DatasetMetricResponse {
  recently_updated: DatasetRecentlyUpdatedResponse[];
  total_counts: Partial<Record<string, DatasetMetric>>;
}

export interface AIProjectCounts {
  candidate_executions: number;
  search_spaces: number;
  models: number;
}

type AIMetricResponse = Partial<Record<string, AIProjectCounts>>;

export function hasDatasetData(data: DatasetMetricResponse) {
  return (
    (data.recently_updated ?? []).length > 0 &&
    Object.values(data.total_counts ?? {}).length
  );
}

interface DatasetMetricsQuery {
  team_id?: string;
}

export class MetricsClient extends RefreshClient {
  private endpoint: string = "/api/v1/metrics";

  protected constructor() {
    super();
  }

  static getInstance() {
    return new this();
  }

  async getDataServiceMetrics() {
    const response = await this.request<DataServiceMetricResponse>({
      url: `/api/v1/metrics/datasvc/per-project-metrics`,
      method: "GET",
    });
    return response.project_stats;
  }

  async getDatasetMetrics(query: DatasetMetricsQuery) {
    const response = await this.request<DatasetMetricResponse>({
      url: `${this.endpoint}/datasvc/per-dataset-metrics`,
      method: "GET",
      params: query,
    });

    return response;
  }

  useGetDataServiceMetrics(
    options?: Omit<
      UseQueryOptions<DataServiceMetricResponse["project_stats"], AxiosError>,
      "queryKey" | "queryFn"
    >
  ) {
    return useQuery({
      ...options,
      queryKey: [`/api/v1/metrics/datasvc/per-project-metrics`],
      queryFn: () => this.getDataServiceMetrics(),
    });
  }

  useGetDatasetMetrics(
    query: DatasetMetricsQuery = {},
    options?: Omit<
      UseQueryOptions<DatasetMetricResponse, AxiosError>,
      "queryKey" | "queryFn"
    >
  ) {
    return useQuery({
      ...options,
      queryKey: [`/api/v1/metrics/datasvc/per-dataset-metrics`, query.team_id],
      queryFn: () => this.getDatasetMetrics(query),
    });
  }

  async getAIServiceMetrics() {
    const response = await this.request<AIMetricResponse>({
      url: `/api/v1/metrics/ai`,
      method: "GET",
    });
    return response;
  }

  useGetAIServiceMetrics(
    options?: Omit<
      UseQueryOptions<AIMetricResponse, AxiosError>,
      "queryKey" | "queryFn"
    >
  ) {
    return useQuery({
      ...options,
      queryKey: [`/api/v1/metrics/ai`],
      queryFn: () => this.getAIServiceMetrics(),
    });
  }

  useGetProjectsMetrics({
    projects,
    projectsQuery,
  }:
    | {
        projects: IProject[];
        projectsQuery?: undefined;
      }
    | {
        projects?: undefined;
        projectsQuery: UseQueryResult<IProject[]>;
      }) {
    const dataCountQuery = this.useGetDataServiceMetrics();
    const aiCountQuery = this.useGetAIServiceMetrics();

    return useMergeQuery(
      [dataCountQuery, aiCountQuery, projectsQuery],
      ([countData, aiCountData, projectsData = projects]) => {
        return projectsData?.map((project) => ({
          id: project.id,
          name: project.name ?? "",
          archived: Boolean(project.archived),
          counts: {
            terminal_materials_count:
              countData[project.id]?.terminal_materials_count ?? 0,
            tables_count: countData[project.id]?.tables_count ?? 0,
            models: aiCountData[project.id]?.models ?? 0,
            search_spaces: aiCountData[project.id]?.search_spaces ?? 0,
            candidate_executions:
              aiCountData[project.id]?.candidate_executions ?? 0,
          },
          activity: {
            last_ingestion_time: "",
            last_ingestion_dataset_name: "",
            last_ingestion_dataset_id: "",
          },
        }));
      }
    );
  }
}
